import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Chip,
  Drawer,
  Fab,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { formatAgo } from '../../components/Alerts/severity';
import { TOP_NAVIGATION_HEIGHT } from '../../constants/style';
import { useAuth } from '../../hooks/useAuth';
import { useConfig } from '../../hooks/useConfig';
import { achTesterEligible } from '../../util/apiHostFor';
import { ChatThread } from './conversationStore';
import FeedbackButtons from './FeedbackButtons';
import { ChatLaunch, subscribeChatLaunch } from './launcher';
import { ChatContext, contextFromLocation, contextPhrase, isPublicPath } from './pageContext';
import { renderMarkdown } from './renderMarkdown';
import { getSamplePrompts, SamplePrompt } from './samplePrompts';
import { useAgentChat } from './useAgentChat';

// Width of the drawer, and of the inset the page gets while it is open.
export const CHAT_DRAWER_WIDTH = 420;
const INSET_VAR = '--ach-chat-inset';

// The FE has no internal-user marker, so the allowlist is the only client-side
// scoping available; the server's 403 is the real enforcement. Both halves must
// hold, and public routes render outside ProtectedRoute so they are excluded here.
export const agentChatEnabled = (params: {
  envFlag: string | undefined;
  userEmail: string | undefined | null;
  allowlistRaw: string | undefined | null;
  pathname: string;
}): boolean => {
  const { envFlag, userEmail, allowlistRaw, pathname } = params;
  return envFlag === 'true' && achTesterEligible(userEmail, allowlistRaw) && !isPublicPath(pathname);
};

const contextLabel = (context: ChatContext): string => {
  const ids = Object.entries(context.entities)
    .map(([key, value]) => `${key.replace('_id', '')} ${value}`)
    .join(' · ');
  if (ids) return `${context.pageType} · ${ids}`;
  return context.pageType === 'generic' ? 'no id pinned' : context.pageType;
};

function TypingIndicator({ status }: { status: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'text.disabled',
            animation: 'achChatBlink 1.2s infinite ease-in-out',
            animationDelay: `${i * 0.16}s`,
            '@keyframes achChatBlink': { '0%, 80%, 100%': { opacity: 0.25 }, '40%': { opacity: 1 } },
          }}
        />
      ))}
      <Typography variant="caption" color="text.secondary">
        {status}
      </Typography>
    </Stack>
  );
}

type BubbleProps = { author: 'user' | 'assistant'; children: React.ReactNode };

function Bubble({ author, children }: BubbleProps) {
  const isUser = author === 'user';
  return (
    <Box
      sx={{
        maxWidth: '88%',
        px: 1.5,
        py: 1,
        borderRadius: 2,
        ...(isUser
          ? {
              alignSelf: 'flex-end',
              borderBottomRightRadius: 4,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
              color: 'text.primary',
            }
          : {
              alignSelf: 'flex-start',
              borderBottomLeftRadius: 4,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 1,
            }),
      }}
    >
      {children}
    </Box>
  );
}

// One drawer opening. `seq` counts openings so each is applied exactly once; `launch`
// is the Ask-AI pin that requested it, and is null for a plain FAB open — which
// reopens whatever thread was active rather than re-applying the last pin.
export type ChatSession = { seq: number; launch: ChatLaunch | null };

type ChatPanelProps = {
  userKey: string;
  achApiHost: string;
  pathname: string;
  open: boolean;
  onClose: () => void;
  session: ChatSession;
};

// ThreadSwitcher lists the recent threads newest-first; selecting one switches without
// touching any of the others (including a run still streaming into them).
function ThreadSwitcher({
  threads,
  activeId,
  onSelect,
}: {
  threads: ChatThread[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title="Recent chats">
        <IconButton
          size="small"
          aria-label="Recent chats"
          data-testid="thread-switcher"
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <ForumOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {threads.map((thread) => (
          <MenuItem
            key={thread.id}
            selected={thread.id === activeId}
            data-testid={`thread-option-${thread.id}`}
            onClick={() => {
              onSelect(thread.id);
              setAnchor(null);
            }}
            sx={{ maxWidth: 320 }}
          >
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {thread.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatAgo(new Date(thread.createdAt).toISOString())}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// A tap fills AND sends: the pills exist to show what the agent can do, and the fastest
// way to show that is an answer — one tap, no second decision. Since a send clears the
// composer, they show only on an empty thread with an empty draft, so a tap can never
// discard something half-typed. Whatever the accessor returns is rendered as-is.
function SamplePromptPills({ prompts, onPick }: { prompts: SamplePrompt[]; onPick: (prompt: string) => void }) {
  if (!prompts.length) return null;
  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      justifyContent="center"
      data-testid="sample-prompts"
      sx={{ mt: 1.5 }}
    >
      {prompts.map((sample) => (
        <Chip
          key={sample.id}
          label={sample.label}
          size="small"
          variant="outlined"
          clickable
          data-testid={`sample-prompt-${sample.id}`}
          onClick={() => onPick(sample.prompt)}
        />
      ))}
    </Stack>
  );
}

const emptyHint = (context: ChatContext): string =>
  context.pageType === 'generic'
    ? 'Pin a campaign or IO to get started — open one and use Ask AI. Or start here:'
    : 'Ask about readiness, pacing, locations, or alerts. Or start here:';

function ChatPanel({ userKey, achApiHost, pathname, open, onClose, session }: ChatPanelProps) {
  const chat = useAgentChat(userKey, achApiHost, pathname);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { active, run, startThread } = chat;
  const { seq, launch } = session;

  useEffect(() => {
    if (seq === 0 || !launch) return;
    startThread(launch.context, launch.draft);
    // `launch` only ever changes together with `seq`, so this runs once per Ask-AI click.
  }, [seq, launch, startThread]);

  const routeContext = contextFromLocation(pathname);
  const offPage = JSON.stringify(routeContext) !== JSON.stringify(active.context);
  const phrase = contextPhrase(active.context);
  const { draft } = active;
  const lastIndex = active.messages.length - 1;

  // Keep the newest turn (and the working indicator) in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [active.id, active.messages.length, run.status, run.error]);

  const submit = () => chat.send(draft);

  return (
    <Box
      data-ach-chat="drawer"
      data-testid="chat-drawer"
      data-open={open ? 'true' : 'false'}
      // Esc is scoped to focus inside the drawer: the alerts page keeps its own Esc for
      // the inspector, and bails when the event came from in here.
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
      sx={{
        width: CHAT_DRAWER_WIDTH,
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          ACH agent
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <ThreadSwitcher threads={chat.threads} activeId={active.id} onSelect={chat.selectThread} />
          <Tooltip title="New chat">
            <IconButton size="small" aria-label="New chat" onClick={chat.newChat}>
              <AddCommentOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" aria-label="Close chat" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Chip size="small" label={contextLabel(active.context)} />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          {phrase ? `Answers about ${phrase}. ` : ''}
          Mention a campaign or IO id to ask about something else. New chat re-pins to the current page.
        </Typography>
        {offPage ? (
          <Typography variant="caption" color="warning.main" component="p" sx={{ mt: 0.5 }}>
            Pinned to the page this chat started on. Start a new chat to use the current page.
          </Typography>
        ) : null}
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 2,
          py: 2,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {active.messages.length === 0 && !run.status ? (
          <Box sx={{ m: 'auto', textAlign: 'center', px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {emptyHint(active.context)}
            </Typography>
            {draft.trim() ? null : (
              <SamplePromptPills prompts={getSamplePrompts(active.context)} onPick={chat.send} />
            )}
          </Box>
        ) : null}

        {active.messages.map((message, index) => (
          // Messages are append-only within a thread, so the index is stable.
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Bubble author={message.role}>
              {message.role === 'assistant' ? (
                renderMarkdown(message.text)
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {message.text}
                </Typography>
              )}
              {message.interrupted ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="warning.main">
                    interrupted
                  </Typography>
                  {index === lastIndex ? (
                    <Button size="small" sx={{ p: 0, minWidth: 0 }} data-testid="chat-resend" onClick={chat.retry}>
                      Resend?
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
            </Bubble>
            {message.role === 'assistant' && message.runId ? (
              <FeedbackButtons
                verdict={message.feedback}
                onVerdict={(verdict, comment) => chat.sendFeedback(index, verdict, comment)}
              />
            ) : null}
          </Box>
        ))}

        {run.status ? (
          <Bubble author="assistant">
            <TypingIndicator status={run.status} />
          </Bubble>
        ) : null}

        {run.error ? (
          <Bubble author="assistant">
            <Typography variant="body2" color="error">
              {run.error}
            </Typography>
            <Button size="small" sx={{ mt: 0.5 }} onClick={chat.retry}>
              Retry
            </Button>
          </Bubble>
        ) : null}

        {run.authExpired ? (
          <Bubble author="assistant">
            <Typography variant="body2" color="error">
              Session expired — sign in again.
            </Typography>
          </Bubble>
        ) : null}
      </Box>

      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-end"
        sx={{ px: 1.5, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={6}
          size="small"
          placeholder={phrase ? `Ask about ${phrase}…` : 'Ask a question — mention a campaign or IO id'}
          inputProps={{ 'aria-label': 'Ask the ACH agent' }}
          value={draft}
          disabled={run.streaming}
          onChange={(e) => chat.setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <IconButton
          color="primary"
          aria-label="Send"
          disabled={run.streaming || !draft.trim()}
          onClick={submit}
          sx={{ mb: 0.25 }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

function ChatDrawer() {
  const { user } = useAuth();
  const { achApiHost } = useConfig();
  const { pathname } = useLocation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<ChatSession>({ seq: 0, launch: null });
  const close = useCallback(() => setOpen(false), []);
  // A launch payload belongs to the Ask-AI click that produced it — the FAB reopens the
  // active thread instead, so a pin is never re-applied behind the user's back.
  const openPlain = useCallback(() => setOpen(true), []);

  useEffect(
    () =>
      subscribeChatLaunch((next) => {
        setSession((prev) => ({ seq: prev.seq + 1, launch: next }));
        setOpen(true);
      }),
    []
  );

  // The drawer is non-modal, so the page has to make room for it rather than sit under
  // it. The inset is a custom property the app shell consumes (Layout), applied from lg
  // up — narrower viewports keep the overlay.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(INSET_VAR, open ? `${CHAT_DRAWER_WIDTH}px` : '0px');
    return () => {
      root.style.removeProperty(INSET_VAR);
    };
  }, [open]);

  // eslint-disable-next-line @typescript-eslint/dot-notation
  const mainNavHeight = theme?.mixins?.toolbar?.['@media (min-width:600px)']?.['minHeight'] || TOP_NAVIGATION_HEIGHT;

  const enabled = agentChatEnabled({
    envFlag: import.meta.env.VITE_AGENT_CHAT_ENABLED,
    userEmail: user?.email,
    allowlistRaw: import.meta.env.VITE_ACH_TESTER_EMAILS,
    pathname,
  });
  if (!enabled) return null;

  return (
    <>
      {/* Hidden while the drawer is open: at narrow widths the two overlap, and the
          drawer already carries its own close. */}
      {!open && (
        <Fab
          size="small"
          color="primary"
          aria-label="Open ACH agent chat"
          onClick={openPlain}
          // Below MUI's modal layer (1300) on purpose: the launcher must never float over
          // a drawer's own footer actions — the alerts inspector drawer duplicates them.
          sx={{ position: 'fixed', right: 16, bottom: 88, zIndex: 1150 }}
        >
          AI
        </Fab>
      )}
      {/* persistent: no backdrop, no close-on-outside-click, and the panel stays mounted
          while closed so a run in flight keeps streaming into its thread. */}
      <Drawer
        anchor="right"
        variant="persistent"
        open={open}
        // Default drawer layer (1200) on purpose: above the page and the FAB, below the
        // modal layer — a temporary drawer, and this panel's own thread menu, must still
        // be able to open over it.
        PaperProps={{ sx: { top: `${mainNavHeight}px`, height: `calc(100% - ${mainNavHeight}px)` } }}
      >
        <ChatPanel
          userKey={user?.email ?? 'anonymous'}
          achApiHost={achApiHost}
          pathname={pathname}
          open={open}
          onClose={close}
          session={session}
        />
      </Drawer>
    </>
  );
}

export default ChatDrawer;
